/** @fileoverview "Selected" tab: source-assigned KPIs grouped by tag with drill-down. */
import { useState, useMemo, useCallback } from "react";
import { parseTags, formatTagKey } from "../Components/TagFilterUtils";
import { buildTrie, traverseTrieMulti, getAvailableKeysMulti } from "./helpers";
import Checkbox from "../../../globalComponents/ui/Checkbox";
import SourcePicker from "./SourcePicker";

/**
 * One expandable tag group listing its source-assigned KPIs with drill-down.
 * @param {object} props Component props.
 * @returns {JSX.Element} The drill node.
 */
function SelectedDrillNode({ keyName, questions, pendingIds, onToggle, savedIds, disabled, sources, kpiSourceMap, onKpiSourceChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [internalPath, setInternalPath] = useState([]);

  const relevantQuestions = useMemo(() => questions.filter(q => {
    if (!pendingIds.has(q.questionId)) return false;
    return parseTags(q.tags).some(c => c.some(t => t?.key === keyName));
  }), [questions, pendingIds, keyName]);

  const trie = useMemo(() => buildTrie(relevantQuestions), [relevantQuestions]);

  const currentKpis = useMemo(() => {
    if (internalPath.length === 0) return relevantQuestions;
    const nodes = traverseTrieMulti(trie, internalPath);
    const validIds = new Set();
    nodes.forEach(n => n?.kpiIds?.forEach(id => validIds.add(id)));
    return relevantQuestions.filter(q => validIds.has(q.questionId));
  }, [relevantQuestions, internalPath, trie]);

  const topValues = useMemo(() => {
    const vals = new Set();
    const km = trie.children?.get(keyName);
    if (km) for (const v of km.keys()) vals.add(v);
    return Array.from(vals).sort();
  }, [trie, keyName]);

  const getOptionCounts = useCallback((key, value, pathPrefix = []) => {
    const matching = relevantQuestions.filter(q => {
      const combos = parseTags(q.tags);
      return combos.some(combo => {
        let pathMatched = true;
        for (const step of pathPrefix) {
          const found = combo.find(t => t?.key === step.key && t?.value === step.value);
          if (!found) { pathMatched = false; break; }
        }
        if (!pathMatched) return false;
        return combo.some(t => t?.key === key && t?.value === value);
      });
    });
    const totalCount = matching.length;
    const assignedCount = matching.filter(q => !!kpiSourceMap[q.questionId]).length;
    return { assignedCount, totalCount };
  }, [relevantQuestions, kpiSourceMap]);

  const pathLevelOptions = useMemo(() => {
    const options = [];
    for (let i = 1; i < internalPath.length; i++) {
      const pathUpTo = internalPath.slice(0, i);
      const nodes = traverseTrieMulti(trie, pathUpTo);
      const vals = new Set();
      for (const node of nodes) {
        const km = node.children?.get(internalPath[i].key);
        if (km) for (const v of km.keys()) vals.add(v);
      }
      options.push({ key: internalPath[i].key, values: Array.from(vals).sort() });
    }
    return options;
  }, [trie, internalPath]);

  const nextAvailableKeyData = useMemo(() => {
    if (internalPath.length === 0) return [];
    const nodes = traverseTrieMulti(trie, internalPath);
    return getAvailableKeysMulti(nodes, new Set([keyName, ...internalPath.map(p => p.key)]));
  }, [trie, internalPath, keyName]);

  const handleDropdownChange = (key, value, index) => {
    const newPath = [...internalPath.slice(0, index)];
    if (value) newPath.push({ key, value });
    setInternalPath(newPath);
  };

  const hasUnsaved = !disabled && relevantQuestions.some(q => !savedIds.has(q.questionId));
  const selectedCount = relevantQuestions.length;
  const assignedCount = useMemo(() =>
    relevantQuestions.filter(q => !!kpiSourceMap[q.questionId]).length,
    [relevantQuestions, kpiSourceMap]
  );

  const assignedKpis = useMemo(() =>
    currentKpis.filter(q => !!kpiSourceMap[q.questionId]),
    [currentKpis, kpiSourceMap]
  );

  return (
    <div style={{ borderBottom: "1px solid var(--es-slate-100)" }}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 20px", cursor: "pointer", background: isOpen ? "var(--es-slate-50)" : "var(--es-white)", transition: "background 0.15s", userSelect: "none" }}
      >
        <div style={{ width: 22, height: 22, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", background: isOpen ? "var(--es-accent)" : "var(--es-slate-200)", transition: "all 0.2s", flexShrink: 0 }}>
          <span style={{ fontSize: 8, color: isOpen ? "var(--es-white)" : "var(--es-slate-500)", transform: isOpen ? "rotate(90deg)" : "none", display: "block", transition: "transform 0.2s" }}>▶</span>
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--es-accent)", textTransform: "uppercase", letterSpacing: "0.8px" }}>{formatTagKey(keyName)}</span>
        {hasUnsaved && <span style={{ fontSize: 9, fontWeight: 700, color: "var(--es-source)", background: "var(--es-amber-100)", border: "1px solid var(--es-amber-300)", borderRadius: 4, padding: "1px 6px" }}>UNSAVED</span>}
        <div style={{ flex: 1 }} />
        <span style={{
          fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 20,
          background: assignedCount === selectedCount ? "var(--es-sky-100)" : "var(--es-amber-100)",
          color: assignedCount === selectedCount ? "var(--es-cyan-700)" : "var(--es-source)",
          border: `1px solid ${assignedCount === selectedCount ? "var(--es-sky-200)" : "var(--es-amber-300)"}`, whiteSpace: "nowrap"
        }}>
          {assignedCount}/{selectedCount}
        </span>
      </div>

      {isOpen && (
        <div style={{ background: "var(--es-slate-50)", borderTop: "1px solid var(--es-slate-100)" }}>
          {topValues.length > 0 && (
            <div style={{ padding: "12px 20px", borderBottom: "1px solid var(--es-slate-100)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginBottom: 8, minHeight: 24 }}>
                {internalPath.length > 0 && (
                  <button onClick={() => setInternalPath([])} style={{ fontSize: 10, color: "var(--es-red-500)", border: "1px solid var(--es-red-200)", background: "var(--es-red-50)", borderRadius: 5, padding: "2px 8px", cursor: "pointer", fontWeight: 600 }}>✕ Reset</button>
                )}
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 180px", minWidth: 0 }}>
                  <label style={{ fontSize: 10, fontWeight: 700, color: "var(--es-slate-500)", display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.4px" }}>{formatTagKey(keyName)}</label>
                  <select
                    style={{ width: "100%", padding: "7px 10px", borderRadius: 7, border: `1.5px solid ${internalPath[0]?.key === keyName ? "var(--es-blue-300)" : "var(--es-slate-200)"}`, fontSize: 12, outline: "none", background: "var(--es-white)", color: "var(--es-gray-700)", cursor: "pointer" }}
                    value={internalPath[0]?.key === keyName ? internalPath[0].value : ""}
                    onChange={e => handleDropdownChange(keyName, e.target.value, 0)}
                  >
                    <option value="">All {formatTagKey(keyName)}s</option>
                    {topValues.map(v => {
                      const { assignedCount, totalCount } = getOptionCounts(keyName, v, []);
                      return <option key={v} value={v}>{v} ({assignedCount}/{totalCount})</option>;
                    })}
                  </select>
                </div>

                {internalPath.length > 1 && pathLevelOptions.map((levelOpts, idx) => (
                  <div key={internalPath[idx + 1].key} style={{ flex: "1 1 180px", minWidth: 0 }}>
                    <label style={{ fontSize: 10, fontWeight: 700, color: "var(--es-slate-500)", display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                      {formatTagKey(internalPath[idx + 1].key)}
                    </label>
                    <select
                      style={{ width: "100%", padding: "7px 10px", borderRadius: 7, border: "1.5px solid var(--es-blue-300)", fontSize: 12, outline: "none", background: "var(--es-white)", color: "var(--es-gray-700)", cursor: "pointer" }}
                      value={internalPath[idx + 1].value}
                      onChange={e => handleDropdownChange(internalPath[idx + 1].key, e.target.value, idx + 1)}
                    >
                      <option value="">All {formatTagKey(internalPath[idx + 1].key)}s</option>
                      {levelOpts.values.map(v => {
                        const { assignedCount, totalCount } = getOptionCounts(levelOpts.key, v, internalPath.slice(0, idx + 1));
                        return (
                          <option key={v} value={v}>
                            {v} ({assignedCount}/{totalCount})
                          </option>
                        );
                      })}
                    </select>
                  </div>
                ))}

                {internalPath.length > 0 && nextAvailableKeyData.length > 0 && (
                  <div style={{ flex: "1 1 180px", minWidth: 0 }}>
                    <label style={{ fontSize: 10, fontWeight: 700, color: "var(--es-accent)", display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.4px" }}>{formatTagKey(nextAvailableKeyData[0].key)}</label>
                    <select
                      style={{ width: "100%", padding: "7px 10px", borderRadius: 7, border: "1.5px solid var(--es-sky-200)", fontSize: 12, outline: "none", background: "var(--es-sky-50)", color: "var(--es-cyan-700)", cursor: "pointer" }}
                      value=""
                      onChange={e => { const [k, v] = e.target.value.split(":::"); if (k && v) handleDropdownChange(k, v, internalPath.length); }}
                    >
                      <option value="">Select {formatTagKey(nextAvailableKeyData[0].key)}...</option>
                      {nextAvailableKeyData.map(group => (
                        <optgroup key={group.key} label={formatTagKey(group.key).toUpperCase()}>
                          {group.values.map(val => {
                            const { assignedCount, totalCount } = getOptionCounts(group.key, val, internalPath);
                            return (
                              <option key={`${group.key}-${val}`} value={`${group.key}:::${val}`}>
                                {val} ({assignedCount}/{totalCount})
                              </option>
                            );
                          })}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {internalPath.length > 0 && (
                <div style={{
                  marginTop: 10, display: "flex", alignItems: "center", gap: 6,
                  padding: "8px 12px", background: "var(--es-sky-50)",
                  borderRadius: 8, border: "1px solid var(--es-sky-200)",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 4, flex: 1, flexWrap: "wrap" }}>
                    {internalPath.map((step, i) => (
                      <span key={step.key} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        {i > 0 && <span style={{ color: "var(--es-sky-200)", fontSize: 13 }}>›</span>}
                        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--es-blue-700)" }}>
                          {step.value}
                        </span>
                      </span>
                    ))}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                    <span style={{ fontSize: 10, color: "var(--es-slate-500)" }}>assigned</span>
                    <span style={{ fontSize: 11, fontWeight: 800, padding: "2px 9px", borderRadius: 10, background: "var(--es-cyan-700)", color: "var(--es-white)" }}>
                      {currentKpis.filter(q => !!kpiSourceMap[q.questionId]).length}
                    </span>
                    <span style={{ fontSize: 10, color: "var(--es-slate-400)" }}>/</span>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 10, background: "var(--es-sky-100)", color: "var(--es-cyan-700)", border: "1px solid var(--es-sky-200)" }}>
                      {currentKpis.length}
                    </span>
                    <span style={{ fontSize: 10, color: "var(--es-slate-500)" }}>selected</span>
                  </div>
                </div>
              )}
            </div>
          )}

          <div style={{ margin: "0 20px 16px", background: "var(--es-white)", border: "1px solid var(--es-slate-200)", borderRadius: 10, overflow: "hidden" }}>
            {assignedKpis.length === 0 ? (
              <div style={{ padding: "24px", textAlign: "center", color: "var(--es-slate-400)", fontSize: 12 }}>No KPIs with assigned sources match this filter.</div>
            ) : (
              <>
                <div style={{ padding: "8px 14px", background: "var(--es-slate-50)", borderBottom: "1px solid var(--es-slate-100)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "var(--es-slate-500)", textTransform: "uppercase", letterSpacing: "0.4px" }}>KPI Title</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    {sources.length > 0 && <span style={{ fontSize: 10, fontWeight: 700, color: "var(--es-source)" }}>Assigned Source</span>}
                    <span style={{ fontSize: 10, fontWeight: 700, color: "var(--es-slate-500)", textTransform: "uppercase", letterSpacing: "0.4px" }}>{assignedKpis.length} item{assignedKpis.length !== 1 ? "s" : ""}</span>
                  </div>
                </div>
                {assignedKpis.map((q, idx) => {
                  const isUnsaved = !savedIds.has(q.questionId);
                  const assignedSourceId = kpiSourceMap[q.questionId] || null;
                  return (
                    <div key={q.questionId} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderBottom: idx === assignedKpis.length - 1 ? "none" : "1px solid var(--es-slate-50)" }}>
                      <Checkbox checked={pendingIds.has(q.questionId)} onChange={c => !disabled && onToggle(q.questionId, c)} disabled={disabled} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 12.5, color: "var(--es-slate-800)", fontWeight: 450, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }} title={q.title}>{q.title}</span>
                      </div>
                      {sources.length > 0 && (
                        <div onClick={e => e.stopPropagation()}>
                          <SourcePicker
                            sources={sources}
                            value={assignedSourceId}
                            onChange={id => onKpiSourceChange(q.questionId, id)}
                            compact
                            disabled={disabled}
                          />
                        </div>
                      )}
                      {isUnsaved && !disabled && (
                        <span style={{ fontSize: 9, fontWeight: 700, color: "var(--es-source)", background: "var(--es-amber-100)", border: "1px solid var(--es-amber-300)", borderRadius: 4, padding: "1px 6px", flexShrink: 0 }}>UNSAVED</span>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * "Selected" tab container: search + per-tag drill-down of source-assigned KPIs.
 * @param {object} props Component props.
 * @returns {JSX.Element} The accordion.
 */
export default function SelectedKpisAccordion({ questions, savedIds, pendingIds, onToggle, disabled, sources, kpiSourceMap, onKpiSourceChange }) {
  const [selectedSearch, setSelectedSearch] = useState("");

  const sourceSelectedQuestions = useMemo(() =>
    questions.filter(q => pendingIds.has(q.questionId) && !!kpiSourceMap[q.questionId]),
    [questions, pendingIds, kpiSourceMap]
  );

  const topLevelKeys = useMemo(() => {
    const keys = new Set();
    questions.filter(q => pendingIds.has(q.questionId)).forEach(q => {
      parseTags(q.tags).forEach(c => { if (c[0]?.key) keys.add(c[0].key); });
    });
    return Array.from(keys).sort();
  }, [questions, pendingIds]);

  const filteredKeys = useMemo(() => {
    if (!selectedSearch.trim()) return topLevelKeys;
    const q = selectedSearch.toLowerCase();
    return topLevelKeys.filter(key => {
      if (formatTagKey(key).toLowerCase().includes(q)) return true;
      return questions.some(kpi =>
        pendingIds.has(kpi.questionId) &&
        parseTags(kpi.tags).some(c => c.some(t => t?.key === key)) &&
        kpi.title?.toLowerCase().includes(q)
      );
    });
  }, [topLevelKeys, selectedSearch, questions, pendingIds]);

  if (pendingIds.size === 0) {
    return (
      <div style={{ padding: "60px 20px", textAlign: "center" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>○</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--es-slate-500)", marginBottom: 6 }}>No KPIs selected</div>
        <div style={{ fontSize: 12, color: "var(--es-slate-400)" }}>Switch to the All KPIs tab to add KPIs.</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ padding: "10px 20px", background: "var(--es-sky-50)", borderBottom: "1px solid var(--es-sky-200)", display: "flex", alignItems: "center", gap: 5 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: "var(--es-cyan-700)" }}>{sourceSelectedQuestions.length}</span>
        <span style={{ fontSize: 13, color: "var(--es-slate-500)" }}>assigned out of</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: "var(--es-cyan-700)" }}>{questions.filter(q => pendingIds.has(q.questionId)).length}</span>
        {Object.keys(kpiSourceMap).length > 0 && (
          <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 600, padding: "2px 10px", borderRadius: 20, background: "var(--es-amber-100)", color: "var(--es-source)", border: "1px solid var(--es-amber-300)" }}>
            ⚡ {Object.values(kpiSourceMap).filter(Boolean).length} source assignments
          </span>
        )}
      </div>

      <div style={{ padding: "10px 20px", borderBottom: "1px solid var(--es-slate-100)" }}>
        <div style={{ position: "relative" }}>
          <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "var(--es-slate-400)", pointerEvents: "none" }}>⌕</span>
          <input
            className="es-input"
            value={selectedSearch}
            onChange={e => setSelectedSearch(e.target.value)}
            placeholder="Search selected KPIs by tag or title…"
            style={{ width: "100%", fontSize: 12, padding: "7px 28px 7px 28px", borderRadius: 8, background: "var(--es-white)", color: "var(--es-slate-800)", outline: "none" }}
          />
          {selectedSearch && (
            <span onClick={() => setSelectedSearch("")} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: "var(--es-slate-400)", cursor: "pointer" }}>×</span>
          )}
        </div>
      </div>

      {filteredKeys.length === 0 ? (
        <div style={{ padding: "24px", textAlign: "center", fontSize: 12, color: "var(--es-slate-400)" }}>
          No results for "{selectedSearch}"
        </div>
      ) : (
        filteredKeys.map(key => (
          <SelectedDrillNode
            key={key} keyName={key} questions={questions} pendingIds={pendingIds}
            onToggle={onToggle} savedIds={savedIds} disabled={disabled}
            sources={sources} kpiSourceMap={kpiSourceMap} onKpiSourceChange={onKpiSourceChange}
          />
        ))
      )}
    </div>
  );
}
